import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calculator, TrendingUp, DollarSign, PiggyBank } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Calculators = () => {
  // EMI Calculator State
  const [emi, setEmi] = useState({
    amount: '',
    rate: '',
    tenure: '',
    result: null
  });

  // Gratuity Calculator State
  const [gratuity, setGratuity] = useState({
    salary: '',
    years: '',
    result: null
  });

  // FD Calculator State
  const [fd, setFd] = useState({
    principal: '',
    rate: '',
    tenure: '',
    frequency: 'quarterly',
    result: null
  });

  // EMI Calculation
  const calculateEMI = () => {
    const P = parseFloat(emi.amount);
    const r = parseFloat(emi.rate) / 12 / 100;
    const n = parseFloat(emi.tenure) * 12;

    if (P > 0 && r > 0 && n > 0) {
      const monthlyEMI = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      const totalAmount = monthlyEMI * n;
      const totalInterest = totalAmount - P;

      setEmi({
        ...emi,
        result: {
          monthlyEMI: Math.round(monthlyEMI),
          totalAmount: Math.round(totalAmount),
          totalInterest: Math.round(totalInterest),
          principal: Math.round(P)
        }
      });
    }
  };

  // Gratuity Calculation
  const calculateGratuity = () => {
    const lastSalary = parseFloat(gratuity.salary);
    const yearsOfService = parseFloat(gratuity.years);

    if (lastSalary > 0 && yearsOfService >= 5) {
      // Formula: (Last Salary × 15 × Years of Service) / 26
      const gratuityAmount = (lastSalary * 15 * yearsOfService) / 26;

      setGratuity({
        ...gratuity,
        result: {
          gratuityAmount: Math.round(gratuityAmount),
          eligibility: yearsOfService >= 5 ? 'Eligible' : 'Not Eligible (Minimum 5 years required)'
        }
      });
    } else if (yearsOfService < 5) {
      setGratuity({
        ...gratuity,
        result: {
          gratuityAmount: 0,
          eligibility: 'Not Eligible (Minimum 5 years of service required)'
        }
      });
    }
  };

  // FD Calculation
  const calculateFD = () => {
    const P = parseFloat(fd.principal);
    const r = parseFloat(fd.rate) / 100;
    const t = parseFloat(fd.tenure);

    if (P > 0 && r > 0 && t > 0) {
      let n;
      switch (fd.frequency) {
        case 'monthly':
          n = 12;
          break;
        case 'quarterly':
          n = 4;
          break;
        case 'half-yearly':
          n = 2;
          break;
        case 'yearly':
          n = 1;
          break;
        default:
          n = 4;
      }

      // Compound Interest Formula: A = P(1 + r/n)^(nt)
      const maturityAmount = P * Math.pow(1 + r / n, n * t);
      const interestEarned = maturityAmount - P;

      setFd({
        ...fd,
        result: {
          maturityAmount: Math.round(maturityAmount),
          interestEarned: Math.round(interestEarned),
          principal: Math.round(P)
        }
      });
    }
  };

  const handleEmiChange = (e) => {
    setEmi({ ...emi, [e.target.name]: e.target.value, result: null });
  };

  const handleGratuityChange = (e) => {
    setGratuity({ ...gratuity, [e.target.name]: e.target.value, result: null });
  };

  const handleFdChange = (e) => {
    setFd({ ...fd, [e.target.name]: e.target.value, result: null });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Header />

      {/* Page Header */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#DC143C] rounded-full mb-6">
            <Calculator className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Financial Calculators
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Plan your finances better with our easy-to-use calculators
          </p>
        </div>
      </section>

      {/* Calculators Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <Tabs defaultValue="emi" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="emi" className="text-base">
                <TrendingUp className="w-4 h-4 mr-2" />
                EMI Calculator
              </TabsTrigger>
              <TabsTrigger value="gratuity" className="text-base">
                <DollarSign className="w-4 h-4 mr-2" />
                Gratuity Calculator
              </TabsTrigger>
              <TabsTrigger value="fd" className="text-base">
                <PiggyBank className="w-4 h-4 mr-2" />
                FD Calculator
              </TabsTrigger>
            </TabsList>

            {/* EMI Calculator */}
            <TabsContent value="emi">
              <Card className="shadow-2xl border-gray-200">
                <CardHeader className="bg-gradient-to-r from-[#2C3E50] to-[#3d5568] text-white rounded-t-lg">
                  <CardTitle className="text-2xl">EMI Calculator</CardTitle>
                  <CardDescription className="text-gray-200">
                    Calculate your Equated Monthly Installment for loans
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Input Section */}
                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="emi-amount" className="text-base font-semibold">
                          Loan Amount (₹)
                        </Label>
                        <Input
                          id="emi-amount"
                          name="amount"
                          type="number"
                          placeholder="e.g., 1000000"
                          value={emi.amount}
                          onChange={handleEmiChange}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="emi-rate" className="text-base font-semibold">
                          Interest Rate (% per annum)
                        </Label>
                        <Input
                          id="emi-rate"
                          name="rate"
                          type="number"
                          step="0.1"
                          placeholder="e.g., 10.5"
                          value={emi.rate}
                          onChange={handleEmiChange}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="emi-tenure" className="text-base font-semibold">
                          Loan Tenure (years)
                        </Label>
                        <Input
                          id="emi-tenure"
                          name="tenure"
                          type="number"
                          placeholder="e.g., 5"
                          value={emi.tenure}
                          onChange={handleEmiChange}
                          className="mt-2"
                        />
                      </div>

                      <Button
                        onClick={calculateEMI}
                        className="w-full bg-[#DC143C] hover:bg-[#B8112A] text-white py-6 text-lg"
                      >
                        Calculate EMI
                      </Button>
                    </div>

                    {/* Result Section */}
                    <div className="bg-gradient-to-br from-gray-50 to-red-50/30 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Results</h3>
                      {emi.result ? (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-4 shadow-sm border-2 border-[#DC143C]">
                            <div className="text-sm text-gray-600 mb-1">Monthly EMI</div>
                            <div className="text-3xl font-bold text-[#DC143C]">
                              ₹{emi.result.monthlyEMI.toLocaleString('en-IN')}
                            </div>
                          </div>

                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-sm text-gray-600 mb-1">Principal Amount</div>
                            <div className="text-xl font-bold text-[#2C3E50]">
                              ₹{emi.result.principal.toLocaleString('en-IN')}
                            </div>
                          </div>

                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-sm text-gray-600 mb-1">Total Interest</div>
                            <div className="text-xl font-bold text-orange-600">
                              ₹{emi.result.totalInterest.toLocaleString('en-IN')}
                            </div>
                          </div>

                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-sm text-gray-600 mb-1">Total Amount Payable</div>
                            <div className="text-xl font-bold text-[#2C3E50]">
                              ₹{emi.result.totalAmount.toLocaleString('en-IN')}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-12">
                          <Calculator className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p>Enter loan details and click Calculate to see results</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Gratuity Calculator */}
            <TabsContent value="gratuity">
              <Card className="shadow-2xl border-gray-200">
                <CardHeader className="bg-gradient-to-r from-[#2C3E50] to-[#3d5568] text-white rounded-t-lg">
                  <CardTitle className="text-2xl">Gratuity Calculator</CardTitle>
                  <CardDescription className="text-gray-200">
                    Calculate your gratuity amount based on salary and years of service
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Input Section */}
                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="gratuity-salary" className="text-base font-semibold">
                          Last Drawn Salary (₹/month)
                        </Label>
                        <Input
                          id="gratuity-salary"
                          name="salary"
                          type="number"
                          placeholder="e.g., 50000"
                          value={gratuity.salary}
                          onChange={handleGratuityChange}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="gratuity-years" className="text-base font-semibold">
                          Years of Service
                        </Label>
                        <Input
                          id="gratuity-years"
                          name="years"
                          type="number"
                          placeholder="e.g., 10"
                          value={gratuity.years}
                          onChange={handleGratuityChange}
                          className="mt-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Minimum 5 years of service required for gratuity eligibility
                        </p>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-gray-700">
                          <strong>Formula:</strong> Gratuity = (Last Salary × 15 × Years of Service) / 26
                        </p>
                      </div>

                      <Button
                        onClick={calculateGratuity}
                        className="w-full bg-[#DC143C] hover:bg-[#B8112A] text-white py-6 text-lg"
                      >
                        Calculate Gratuity
                      </Button>
                    </div>

                    {/* Result Section */}
                    <div className="bg-gradient-to-br from-gray-50 to-green-50/30 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Results</h3>
                      {gratuity.result ? (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-6 shadow-sm border-2 border-green-600">
                            <div className="text-sm text-gray-600 mb-2">Gratuity Amount</div>
                            <div className="text-4xl font-bold text-green-600">
                              ₹{gratuity.result.gratuityAmount.toLocaleString('en-IN')}
                            </div>
                          </div>

                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-sm text-gray-600 mb-1">Eligibility Status</div>
                            <div className={`text-lg font-bold ${gratuity.result.eligibility.includes('Not') ? 'text-red-600' : 'text-green-600'}`}>
                              {gratuity.result.eligibility}
                            </div>
                          </div>

                          {gratuity.result.gratuityAmount > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <p className="text-sm text-gray-700">
                                This is the approximate gratuity amount you are entitled to receive upon completion of service or retirement.
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-12">
                          <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p>Enter your details and click Calculate to see results</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* FD Calculator */}
            <TabsContent value="fd">
              <Card className="shadow-2xl border-gray-200">
                <CardHeader className="bg-gradient-to-r from-[#2C3E50] to-[#3d5568] text-white rounded-t-lg">
                  <CardTitle className="text-2xl">Fixed Deposit Calculator</CardTitle>
                  <CardDescription className="text-gray-200">
                    Calculate your FD maturity amount with compound interest
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Input Section */}
                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="fd-principal" className="text-base font-semibold">
                          Principal Amount (₹)
                        </Label>
                        <Input
                          id="fd-principal"
                          name="principal"
                          type="number"
                          placeholder="e.g., 100000"
                          value={fd.principal}
                          onChange={handleFdChange}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="fd-rate" className="text-base font-semibold">
                          Interest Rate (% per annum)
                        </Label>
                        <Input
                          id="fd-rate"
                          name="rate"
                          type="number"
                          step="0.1"
                          placeholder="e.g., 7.5"
                          value={fd.rate}
                          onChange={handleFdChange}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="fd-tenure" className="text-base font-semibold">
                          Tenure (years)
                        </Label>
                        <Input
                          id="fd-tenure"
                          name="tenure"
                          type="number"
                          placeholder="e.g., 3"
                          value={fd.tenure}
                          onChange={handleFdChange}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="fd-frequency" className="text-base font-semibold">
                          Compounding Frequency
                        </Label>
                        <select
                          id="fd-frequency"
                          name="frequency"
                          value={fd.frequency}
                          onChange={handleFdChange}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#DC143C]"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="half-yearly">Half-Yearly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>

                      <Button
                        onClick={calculateFD}
                        className="w-full bg-[#DC143C] hover:bg-[#B8112A] text-white py-6 text-lg"
                      >
                        Calculate FD Maturity
                      </Button>
                    </div>

                    {/* Result Section */}
                    <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Results</h3>
                      {fd.result ? (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-6 shadow-sm border-2 border-blue-600">
                            <div className="text-sm text-gray-600 mb-2">Maturity Amount</div>
                            <div className="text-4xl font-bold text-blue-600">
                              ₹{fd.result.maturityAmount.toLocaleString('en-IN')}
                            </div>
                          </div>

                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-sm text-gray-600 mb-1">Principal Amount</div>
                            <div className="text-xl font-bold text-[#2C3E50]">
                              ₹{fd.result.principal.toLocaleString('en-IN')}
                            </div>
                          </div>

                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-sm text-gray-600 mb-1">Interest Earned</div>
                            <div className="text-xl font-bold text-green-600">
                              ₹{fd.result.interestEarned.toLocaleString('en-IN')}
                            </div>
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-gray-700">
                              <strong>Returns:</strong> {((fd.result.interestEarned / fd.result.principal) * 100).toFixed(2)}% total returns on your investment
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-12">
                          <PiggyBank className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p>Enter FD details and click Calculate to see results</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Calculators;
